import { IMPORT_STATUSES, type ImportInfo } from '@mcgames/contracts';
import { Badge, Button, Select, Spinner } from '@mcgames/ui';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { formatDateTime } from '../lib/format-date.ts';
import styles from './Admin.module.css';
import { CancelImportButton } from './games/CancelImportButton.tsx';
import { DuplicateActions } from './games/DuplicateActions.tsx';
import { ErrorText } from './games/ErrorText.tsx';
import { importReasonDetail, importStatusTone } from './indexer/import-text.ts';
import { useImports, useRetryListedImport, type ImportsFilter } from './imports-hooks.ts';

/** Every indexer import, newest first; running ones can be cancelled, stopped ones retried. */
export function ImportsPage() {
  const { t } = useTranslation('admin');
  const [status, setStatus] = useState<ImportsFilter>('');
  const imports = useImports(status);

  return (
    <div className={styles['page']}>
      <h1 className={styles['heading']}>{t('imports.title')}</h1>
      <p className={styles['muted']}>{t('imports.intro')}</p>
      <div className={styles['row']}>
        <Select<ImportsFilter>
          label={t('imports.filter')}
          value={status}
          options={[
            { value: '', label: t('imports.all') },
            ...IMPORT_STATUSES.map((value) => ({ value, label: t(`imports.status.${value}`) })),
          ]}
          onChange={setStatus}
        />
      </div>
      {imports.isPending && <Spinner />}
      {imports.isError && (
        <p role="alert" className={styles['error']}>
          {t('imports.loadFailed')}
        </p>
      )}
      {imports.isSuccess &&
        (imports.data.length === 0 ? (
          <p role="status" className={styles['muted']}>
            {t('imports.empty')}
          </p>
        ) : (
          <ImportsTable imports={imports.data} />
        ))}
    </div>
  );
}

function ImportsTable({ imports }: { imports: ImportInfo[] }) {
  const { t } = useTranslation('admin');
  return (
    <div className={styles['tableWrap']}>
      <table className={styles['table']} aria-label={t('imports.label')}>
        <thead>
          <tr>
            <th scope="col">{t('imports.cols.release')}</th>
            <th scope="col">{t('imports.cols.game')}</th>
            <th scope="col">{t('imports.cols.kind')}</th>
            <th scope="col">{t('imports.cols.status')}</th>
            <th scope="col">{t('imports.cols.step')}</th>
            <th scope="col">{t('imports.cols.reason')}</th>
            <th scope="col">{t('imports.cols.startedBy')}</th>
            <th scope="col">{t('imports.cols.updated')}</th>
            <th scope="col">{t('imports.cols.action')}</th>
          </tr>
        </thead>
        <tbody>
          {imports.map((info) => (
            <ImportRow key={info.id} info={info} />
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ImportRow({ info }: { info: ImportInfo }) {
  const { t, i18n } = useTranslation('admin');
  const stopped = info.status === 'needs_review' || info.status === 'failed';
  const detail = importReasonDetail(info);
  return (
    <tr>
      <td className={styles['wrap']}>{info.releaseTitle}</td>
      <td className={styles['wrap']}>
        {info.gameId === null ? (
          '-'
        ) : (
          <>
            <Link to={`/admin/games/${encodeURIComponent(info.gameId)}`}>
              {info.gameTitle ?? info.gameId}
            </Link>
            {info.versionName !== null && <div className={styles['muted']}>{info.versionName}</div>}
          </>
        )}
      </td>
      <td className={styles['nowrap']}>{t(`imports.kind.${info.kind}`)}</td>
      <td className={styles['nowrap']}>
        <Badge tone={importStatusTone(info.status)}>{t(`imports.status.${info.status}`)}</Badge>
      </td>
      <td className={styles['nowrap']}>{t(`indexer.step.${info.step}`)}</td>
      <td className={styles['wrap']}>
        {info.reason !== null && t(`indexer.reason.${info.reason}`)}
        {info.reason === 'already_in_catalog' && info.reasonDetail !== null && (
          <span className={styles['muted']}>
            {' '}
            (
            {info.duplicateGameId !== null ? (
              <Link to={`/admin/games/${encodeURIComponent(info.duplicateGameId)}`}>
                {t('imports.existingGame', { title: info.reasonDetail })}
              </Link>
            ) : (
              t('imports.existingGame', { title: info.reasonDetail })
            )}
            )
          </span>
        )}
        {detail !== null && <div className={styles['cellDetail']}>{detail}</div>}
      </td>
      <td className={styles['wrap']}>
        {t(`imports.startedBy.${info.createdBy.kind}`, { name: info.createdBy.name })}
      </td>
      <td className={styles['nowrap']}>{formatDateTime(info.updatedAt, i18n.language)}</td>
      <td>
        {info.status === 'running' && <CancelImportButton info={info} size="sm" />}
        {stopped &&
          (info.reason === 'already_in_catalog' ? (
            <DuplicateActions info={info} size="sm" />
          ) : (
            <RetryButton info={info} />
          ))}
      </td>
    </tr>
  );
}

function RetryButton({ info }: { info: ImportInfo }) {
  const { t } = useTranslation('admin');
  const retry = useRetryListedImport(info);
  return (
    <>
      <Button
        variant="secondary"
        size="sm"
        disabled={retry.isPending}
        onClick={() => retry.mutate()}
      >
        {t('imports.retry')}
      </Button>
      <ErrorText error={retry.error} />
    </>
  );
}
